import React, { useCallback, useEffect, useRef, useState } from 'react';
import DefaultCard, { findDefaultCard } from './DefaultCard';
import { Cart } from '../../../Capsules/Envelopes';
import { Wallet } from '../../../../../../providers/DonorProvider/interfaces';
import { useDonorContext } from '../../../../../../providers/DonorProvider/DonorProvider';
import { logging } from '../../../../../../utils/logError';
import { Text } from '../../../common/Text/Text';
import ReCaptchaV3 from '../../../../../Wrappers/ReCaptchaV3';
import { AuthModal } from '../../../../../authAndAddCardFlow';
import { LogLevel } from '../../../../../../utils/logger/models';
import type { ErrorResponse } from '../../../../../../api';
import { ApiHandler, useDonorProfile } from '../../../../../../api';
import EmailVerificationError from '../../EmailVerificationError';
import { DISABLE_CAPTCHA, getTotalCartAmount } from '../../../../../../utils';
import { Button } from '../../../../../ui';

const verifyEmailErrorCode = 1293;
const observerOption: IntersectionObserverInit = {
    root: null,
    rootMargin: '-86px',
    threshold: 0,
};

interface GiveButtonProps {
    cart: Cart;
    errorState: ErrorResponse | undefined;
    disabled?: boolean;
    setCart: React.Dispatch<React.SetStateAction<Cart>>;
    onSubmit: () => unknown;
    setWallet: (wallet: Wallet | undefined) => unknown;
}

const defaultErrorMessage = 'Unable to send the donation, please try again.';
const GiveButton: React.FC<GiveButtonProps> = ({
    cart,
    disabled,
    errorState,
    setWallet,
    onSubmit,
}) => {
    const domDetectRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [addPaymentOpen, setAddPaymentOpen] = useState<boolean>(false);
    const [resendEmailLoading, setResendEmailLoading] =
        useState<boolean>(false);
    const [resendEmailSuccess, setResendEmailSuccess] =
        useState<boolean>(false);
    const [resendEmailError, setResendEmailError] = useState<
        string | undefined
    >(undefined);
    const { setDonorContext } = useDonorContext();
    const { donorProfile, updateLocalDonorProfile } = useDonorProfile();
    const isLoggedIn = Boolean(donorProfile);
    const wallet = isLoggedIn ? donorProfile!.wallets : undefined;
    const donor = isLoggedIn ? donorProfile : undefined;
    const handleOpen = () => setOpen(true);
    const handlePaymentOpen = () => setAddPaymentOpen(true);

    useEffect(() => {
        setWallet(wallet ? findDefaultCard(wallet) : undefined);
    }, [wallet, setWallet]);

    useEffect(() => {
        const dom = domDetectRef.current;
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, observerOption);

        if (dom) {
            observer.observe(dom);
        }

        return () => {
            if (dom) {
                observer.unobserve(dom);
            }
        };
    }, []);

    const addCard = useCallback(
        (card: Wallet) => {
            setDonorContext((s) => {
                const newState = { ...s };
                if (!donorProfile) {
                    logging(
                        'No user information available while adding card.',
                        {
                            donorId:
                                s.loginResponse?.data?.donorId || undefined,
                            ogaVersions: '4.0',
                        },
                        LogLevel.EMERGENCY,
                    );
                    return s;
                }
                if (!donorProfile?.wallets) {
                    updateLocalDonorProfile({
                        ...donorProfile,
                        wallets: [card],
                    });
                } else {
                    updateLocalDonorProfile({
                        ...donorProfile,
                        wallets: [...donorProfile.wallets, card],
                    });
                }
                return newState;
            });
            setWallet(card);
        },
        [donorProfile, setDonorContext, setWallet, updateLocalDonorProfile],
    );

    const resendVerificationEmail = useCallback(async () => {
        if (!donor?.donor.email) {
            setResendEmailError('Unable to find email address');
            return;
        }
        setResendEmailLoading(true);
        setResendEmailError(undefined);
        setResendEmailSuccess(false);
        const apiRequest = await ApiHandler.instance.auth.resendActivateEmail({
            emailAddress: donor.donor.email,
        });
        if (apiRequest.success) {
            setResendEmailSuccess(true);
        } else {
            setResendEmailError(
                apiRequest.error?.message ||
                    'Unable to resend verification email',
            );
        }
        setResendEmailLoading(false);
    }, [donor]);

    const isEmailVerificationError =
        errorState && errorState.status === verifyEmailErrorCode;

    return (
        <>
            <div ref={domDetectRef} />
            <div
                className={`border-0 border-t border-solid border-utility-separator px-6 pb-6 ${
                    isVisible
                        ? ''
                        : 'mobile:fixed mobile:bottom-0 mobile:z-[2] mobile:w-full mobile:px-2 mobile:pb-[13px] mobile:bg-white'
                }`}
            >
                {errorState && !isEmailVerificationError && (
                    <Text
                        className={`pt-[14px] text-center text-utility-error ${
                            isVisible ? '' : 'mobile:text-[13px]'
                        }`}
                    >
                        {errorState.message || defaultErrorMessage}
                    </Text>
                )}
                {isEmailVerificationError && (
                    <EmailVerificationError
                        resendEmailLoading={resendEmailLoading}
                        resendEmailSuccess={resendEmailSuccess}
                        resendEmailError={resendEmailError}
                        email={donor?.donor.email}
                        onResend={resendVerificationEmail}
                    />
                )}
                <DefaultCard
                    addPaymentOpen={addPaymentOpen}
                    setAddPaymentOpen={setAddPaymentOpen}
                    wallet={wallet}
                    addCard={addCard}
                    selectedCard={cart.wallet}
                    setCard={setWallet}
                />
                {!DISABLE_CAPTCHA && <ReCaptchaV3 hideText={true} />}
                <Button
                    variant="give"
                    className={`block w-full mx-auto max-w-[323px] ${
                        isLoggedIn
                            ? 'mt-[6px]'
                            : isVisible
                              ? 'mt-6'
                              : 'mt-6 mobile:mt-2'
                    }`}
                    disabled={disabled}
                    onClick={
                        cart.wallet
                            ? onSubmit
                            : isLoggedIn
                              ? handlePaymentOpen
                              : handleOpen
                    }
                >
                    GIVE NOW
                </Button>
            </div>
            <AuthModal
                open={open}
                setOpen={setOpen}
                donation={getTotalCartAmount(cart.envelopes).amount.toString()}
            />
        </>
    );
};

export default GiveButton;
